import React, { useEffect, useMemo } from 'react';
import { BsPlus, BsStar, BsStarFill } from 'react-icons/bs';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Flex, HStack, Icon, Text } from '@chakra-ui/react';

import { CypressIds } from '../../../../../../../../constants/cypressIds';
import { inAppText } from '../../../../../../../../constants/inAppText';
import { TabNames } from '../../../../../../../../constants/tabMenuTypes';
import { selectCompanyId } from '../../../../../../../../redux/modules/company';
import { hideTray, showTray } from '../../../../../../../../redux/modules/tray';
import colors from '../../../../../../../../styles/theme/foundation/colors';
import RequirementTemplateBlueprintType from '../../../../../../../../types/RequirementTemplateBlueprintType';
import SearchInputGroup from '../../../../../../../SearchInputGroup';
import {
  customPolicyOptions,
  REQUIREMENT_TYPES
} from '../../../../../../util/options';
import { getCreateRequirementTemplateBlockParams } from '../../../../../../util/util';
import { RequirementMenuItem, RequirementPropertyTypeMenu } from '../../../../../RequirementPropertyTypeMenu';
import RequirementTypesFilters from '../../../../../RequirementTypesFilters';

import CreateCustomPolicyTypeTray from './components/CreateCustomPolicyTypeTray';
import SetPolicyFieldValuesTray from './components/SetPolicyFieldValuesTray';

const TemplateBuilderPolicyPanel = ({ blueprint }) => {
  const dispatch = useDispatch();
  const companyId = useSelector(selectCompanyId);
  const currentTemplate = useSelector(blueprint.selectCurrentRequirementTemplate);

  const currentRequirementTemplateBlocks = useSelector(blueprint.selectCurrentRequirementTemplateBlocks);
  let currentMaxOrder = 0;

  useEffect(() => {
    if (currentRequirementTemplateBlocks) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      currentMaxOrder = Math.max(...currentRequirementTemplateBlocks.map(templateBlock => templateBlock.order));
    }
  }, [currentRequirementTemplateBlocks, dispatch]);

  useEffect(() => {
    if (companyId && currentTemplate) {
      dispatch(blueprint.listInsurancePolicyTypes());
      dispatch(blueprint.listDeletedPolicyTypes());
    }
  }, [companyId, currentTemplate?.id, dispatch]);

  const insurancePolicyTypes = useSelector(blueprint.selectInsurancePolicyTypes);

  const handleSearchChange = e => {
    const searchValue = e.target.value;
    dispatch(blueprint.updateInsurancePolicyTypeSearchFilter(searchValue));
  };

  const handleFilterChange = (isChecked, type) => {
    dispatch(blueprint.setInsurancePolicyTypeFilters({ ...insurancePolicyTypes.filters, [type]: isChecked }));
  };

  const showCreateCustomPolicyTypeTray = () => {
    dispatch(blueprint.resetCreateCustomPolicyType());
    dispatch(blueprint.setCreateCustomStep(0, TabNames.Policies));
    dispatch(showTray(
      () => <CreateCustomPolicyTypeTray blueprint={blueprint} />,
      {
        onClose: () => {
          dispatch(hideTray());
        }
      }
    ));
  };

  const insurancePolicyTypeTabs = useMemo(() => insurancePolicyTypes?.data.map(policyTypeData => ({
    id: policyTypeData?.id,
    tabId: policyTypeData?.name,
    title: policyTypeData?.displayName,
    description: policyTypeData?.description,
    icon: policyTypeData?.favorite ? BsStarFill : BsStar,
    isFavorite: policyTypeData?.favorite ?? 0,
    onClick: () => {
      const response = dispatch(blueprint.createRequirementTemplateBlock(getCreateRequirementTemplateBlockParams(
        currentMaxOrder,
        REQUIREMENT_TYPES.Policies,
        { properties: [] }
      )));

      if (response) {
        dispatch(blueprint.setCurrentPolicyType(policyTypeData));

        response.then(result => {
          dispatch(blueprint.setCurrentRequirementTemplateBlock(result.id));
          dispatch(showTray(
            () => <SetPolicyFieldValuesTray blueprint={blueprint} currentPolicyTypeId={policyTypeData?.id} />,
            {
              onClose: async () => {
                const currentTemplateBlock = result;
                if (!currentTemplateBlock.length
                      || !currentTemplateBlock?.requirementTemplateBlockProperties.filter(p => p.value).length) {
                  await dispatch(blueprint.deleteRequirementTemplateBlock(currentTemplateBlock?.id));
                }

                dispatch(blueprint.setCurrentRequirementTemplateBlock(null));
                dispatch(blueprint.resetCurrentPolicyType());
                dispatch(hideTray());
              }
            }
          ));
        });
      }
    },
    onFavoriteButtonCLick: () => (policyTypeData?.favorite
      ? dispatch(blueprint.removePolicyTypeFavorite(policyTypeData?.id))
      : dispatch(blueprint.addPolicyTypeFavorite(policyTypeData?.id))),
    submenu: policyTypeData?.companyId ? customPolicyOptions : null
  })), [currentMaxOrder, dispatch, insurancePolicyTypes.data]);

  const pageData = insurancePolicyTypes?.pageInfo
    ? { pageNumber: insurancePolicyTypes.pageInfo.currentPage + 1 } : null;

  return (
    <Flex direction="column" h="100%" justify="flex-start" w="100%">
      <Box mt={4} px={5} py={4}>
        <Text color={colors.greyScale['500']} textAlign="left" textStyle="bodySm">
          {inAppText.templatesPage.templateBuilder.policiesPane.addPolicyInstructions}
        </Text>
        <Button
          leftIcon={<Icon as={BsPlus} />}
          mt={4}
          variant="secondary"
          w="100%"
          onClick={showCreateCustomPolicyTypeTray}
        >
          Create Custom Policy
        </Button>
        <HStack justify="space-around" mt={4} spacing={1} width="100%">
          <SearchInputGroup h="40px" onChange={handleSearchChange} />
          <RequirementTypesFilters
            currentFilters={insurancePolicyTypes.filters}
            type="Policies"
            onChange={handleFilterChange}
            onReset={() => dispatch(blueprint.listInsurancePolicyTypes())}
          />
        </HStack>
      </Box>
      {insurancePolicyTypeTabs?.length > 0
      && (
      <RequirementPropertyTypeMenu
        blueprint={blueprint}
        cypressIdFunction={CypressIds.policyTypeMenuOption}
        fetchNext={() => dispatch(blueprint.listInsurancePolicyTypes({ pageNumber: insurancePolicyTypes.currentPage + 1, pageSize: 25 }))}
        hasMore={insurancePolicyTypes.currentPage !== insurancePolicyTypes?.lastPage}
      >
        {insurancePolicyTypeTabs.map(option => (
          <RequirementMenuItem
            {...option}
            key={option.id}
            subMenu={option?.submenu}
          />
        ))}
      </RequirementPropertyTypeMenu>
      )}
    </Flex>
  );
};

TemplateBuilderPolicyPanel.propTypes = { blueprint: RequirementTemplateBlueprintType };

export default TemplateBuilderPolicyPanel;
